import { useState, useMemo } from "react";
import { Button, CircularProgress, Box, Typography, TextField } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import Event from "../../components/Event";
import EventEdit from "../EventEdit";
import { EventButtonSx, BtnSx } from "./styles";
import { useEvents } from "../../hooks/useEvents";
import { darkFieldSx } from "../ArtistEdit/style";

const EventsTab = () => {
  const [editEvent, setEditEvent] = useState({ modalOpen: false, id: undefined });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [mainPageOnly, setMainPageOnly] = useState(false);

  const handleDateFrom = (val) => { setDateFrom(val); setMainPageOnly(false); };
  const handleDateTo = (val) => { setDateTo(val); setMainPageOnly(false); };
  const handleMainPageToggle = () => {
    const next = !mainPageOnly;
    setMainPageOnly(next);
    if (next) { setDateFrom(""); setDateTo(""); }
  };

  const filters = useMemo(() => {
    if (mainPageOnly) return { showOnMainPage: true };
    const f = {};
    if (dateFrom) f.dateFrom = dateFrom;
    if (dateTo) f.dateTo = dateTo;
    return f;
  }, [mainPageOnly, dateFrom, dateTo]);

  const { data, isLoading, hasMore, loadMore } = useEvents(filters);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography
          variant="overline"
          sx={{ color: "rgba(255,255,255,0.4)", letterSpacing: "0.2em", fontSize: "0.68rem" }}
        >
          Events
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setEditEvent({ modalOpen: true, id: undefined })}
          sx={BtnSx}
        >
          + Add Event
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        <Button
          size="small"
          variant={mainPageOnly ? "contained" : "outlined"}
          startIcon={<StarIcon sx={{ fontSize: "14px !important" }} />}
          onClick={handleMainPageToggle}
          sx={{
            fontSize: "0.72rem",
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "none",
            height: 40,
            px: 2,
            ...(mainPageOnly
              ? { backgroundColor: "white", color: "#0a0a0a", "&:hover": { backgroundColor: "rgba(255,255,255,0.85)" } }
              : { borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.55)", "&:hover": { borderColor: "rgba(255,255,255,0.5)", color: "white" } }),
          }}
        >
          Featured Event
        </Button>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => handleDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...darkFieldSx, width: 150 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => handleDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...darkFieldSx, width: 150 }}
          />
        </Box>
        {(dateFrom || dateTo || mainPageOnly) && (
          <Button
            size="small"
            onClick={() => { setDateFrom(""); setDateTo(""); setMainPageOnly(false); }}
            sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem", textTransform: "none", "&:hover": { color: "white" } }}
          >
            Clear
          </Button>
        )}
      </Box>

      {isLoading && !data.length ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "rgba(255,255,255,0.35)" }} />
        </Box>
      ) : !data.length ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.85rem" }}>
            No events found
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {data.map(({ name, date, location, _id, imageURL }) => (
            <Box
              key={_id}
              onClick={() => setEditEvent({ modalOpen: true, id: _id })}
            >
              <Event admin={true} image={imageURL} title={name} date={date} location={location} id={_id} />
            </Box>
          ))}
        </Box>
      )}

      {hasMore && (
        <Button
          variant="outlined"
          onClick={loadMore}
          disabled={isLoading}
          sx={EventButtonSx}
        >
          {isLoading && data.length ? (
            <CircularProgress size={14} sx={{ color: "rgba(255,255,255,0.4)" }} />
          ) : (
            "Load More"
          )}
        </Button>
      )}

      <EventEdit
        open={editEvent.modalOpen}
        eventId={editEvent.id}
        closeModal={() => setEditEvent({ modalOpen: false, id: undefined })}
      />
    </>
  );
};

export default EventsTab;
